import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { Settings, Lock, Shield, RefreshCw, LogOut } from 'lucide-react';
import SocketService from '../../../../packages/shared/socket';
import { useAuth } from './AuthGate';
import { useTranslation } from '../hooks/useTranslation.jsx';
import { isPinSessionValid } from '../utils/pinSession';
import { clearTown } from '../utils/participantTown';

// Settings button with connection status shown via border color
const SettingsButton = styled.button`
  position: ${(props) => (props.$inline ? 'static' : 'fixed')};
  top: 16px;
  right: 16px;
  width: ${(props) => (props.$inline ? '32px' : '40px')};
  height: ${(props) => (props.$inline ? '32px' : '40px')};
  flex: 0 0 auto;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  border: 2px solid ${props => {
    if (!props.$hasInstance) return '#555';
    return props.$connected ? '#00ff88' : '#ff4444';
  }};
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 900;

  &:hover {
    background: rgba(0, 0, 0, 0.6);
    transform: scale(1.05);
  }

  box-shadow: ${props => {
    if (!props.$hasInstance) return 'none';
    return props.$connected ? '0 0 10px rgba(0, 255, 136, 0.25)' : '0 0 10px rgba(255, 68, 68, 0.25)';
  }};
`;

const Dropdown = styled.div`
  position: fixed;
  top: 64px;
  right: 16px;
  background: rgba(26, 26, 26, 0.95);
  backdrop-filter: blur(12px);
  border: 1px solid #333;
  border-radius: 12px;
  min-width: 200px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  z-index: 901;
  animation: slideDown 0.15s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ConnectionStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #333;
  font-size: 13px;
  color: ${props => props.$connected ? '#00ff88' : '#ff4444'};
`;

const DropdownSection = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid #333;

  &:last-child {
    border-bottom: none;
  }
`;

const DropdownItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: transparent;
  border: none;
  color: ${props => props.$highlight ? '#0099ff' : '#fff'};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  &:hover {
    background: ${props => props.$highlight ? 'rgba(0, 153, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)'};
  }

  svg {
    opacity: 0.6;
  }
`;

// Tiny unlock button when locked
const UnlockButton = styled.button`
  position: fixed;
  bottom: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.08);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  opacity: 0.3;

  &:hover {
    color: rgba(255, 255, 255, 0.4);
    opacity: 1;
  }
`;

const UnlockModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  backdrop-filter: blur(8px);
`;

const UnlockBox = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 2px solid #333;
  border-radius: 16px;
  padding: 32px;
  width: 320px;
  text-align: center;
`;

const UnlockTitle = styled.h3`
  color: #fff;
  font-size: 18px;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const UnlockSubtitle = styled.p`
  color: #666;
  font-size: 14px;
  margin: 0 0 24px 0;
`;

const PinInput = styled.input`
  width: 100%;
  padding: 14px;
  background: rgba(0, 0, 0, 0.3);
  border: 2px solid ${props => props.$error ? '#ff4444' : '#333'};
  border-radius: 10px;
  color: #fff;
  font-size: 20px;
  font-family: 'JetBrains Mono', monospace;
  text-align: center;
  letter-spacing: 4px;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: ${props => props.$error ? '#ff4444' : '#0099ff'};
  }

  &::placeholder {
    color: #444;
    letter-spacing: 2px;
  }
`;

const ErrorText = styled.div`
  color: #ff4444;
  font-size: 13px;
  margin-top: 12px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const Button = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${props => props.$primary ? `
    background: linear-gradient(135deg, #0099ff, #0066cc);
    color: #fff;
    &:hover { background: linear-gradient(135deg, #00aaff, #0077dd); }
  ` : `
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid #333;
    color: #888;
    &:hover { border-color: #555; color: #fff; }
  `}
`;

/**
 * GlobalSettings - Unified settings button for all screens
 *
 * Uses AuthContext for auth state.
 * Shows:
 * - Settings icon with connection status via border color
 * - Dropdown with admin mode link, refresh connection
 * - Tiny unlock button when device is locked
 */
/**
 * `inline`: the button sits in the flow of a header's own bar instead of floating
 * at the top right, so it never covers the bar's controls; the dropdown still opens
 * under the top-right corner.
 */
const GlobalSettings = ({ onSettingsClick, inline = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isLocked, isAdmin, unlock, logout, user } = useAuth();

  const [isConnected, setIsConnected] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Get instance ID from URL or localStorage
  const getInstanceId = () => {
    const params = new URLSearchParams(location.search);
    return params.get('instance') || localStorage.getItem('kuutio-admin-instance');
  };

  const instanceId = getInstanceId();

  // Connect to socket for status
  useEffect(() => {
    if (!instanceId) {
      setIsConnected(false);
      return;
    }

    const checkConnection = () => {
      setIsConnected(SocketService.isConnected());
    };

    checkConnection();
    const interval = setInterval(checkConnection, 1000);

    const socket = SocketService.socket;
    if (socket) {
      const handleConnect = () => setIsConnected(true);
      const handleDisconnect = () => setIsConnected(false);

      socket.on('connect', handleConnect);
      socket.on('disconnect', handleDisconnect);

      return () => {
        clearInterval(interval);
        socket.off('connect', handleConnect);
        socket.off('disconnect', handleDisconnect);
      };
    }

    return () => clearInterval(interval);
  }, [instanceId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRefreshConnection = () => {
    if (SocketService.socket) {
      SocketService.socket.disconnect();
      SocketService.socket.connect();
    }
    setShowDropdown(false);
  };

  const handleAdminMode = () => {
    setShowDropdown(false);
    if (instanceId) {
      navigate(`/admin?instance=${instanceId}`);
    } else {
      navigate('/admin');
    }
  };

  // Sign out of a PIN session on this device: the next person starts from the
  // chooser (URL → PIN → town → begin). AuthGate.logout ends the server session
  // and the PIN timestamp; the device keys the /screen flow and the town picker
  // wrote are cleared here, or the auto-resume walks straight back in.
  // A signed-in user (Entra or e-mail code) has the avatar menu's own sign-out,
  // so the gear offers one only for a bare PIN session: two menus with the same
  // item read as two profiles (28.10.2026).
  const hasPinSession = isPinSessionValid() && !user;
  const handleSignOut = () => {
    setShowDropdown(false);
    const params = new URLSearchParams(location.search);
    const kid = params.get('kid') || params.get('instance') || localStorage.getItem('kuutio-participant-instance');
    if (kid) clearTown(kid);
    ['kuutio-instance', 'kuutio-participant-instance', 'kuutio-participant-auth', 'kuutio-screen-admin',
      'kuutio-admin-auth', 'kuutio-device-locked', 'kuutio-admin-instance']
      .forEach((k) => { try { localStorage.removeItem(k); } catch { /* storage disabled */ } });
    if (SocketService.socket) SocketService.socket.disconnect();
    logout();
    navigate('/', { replace: true });
  };

  const handleUnlock = async () => {
    if (!pin.trim() || loading) return;

    setLoading(true);
    setError('');

    const success = await unlock(pin.trim());

    if (success) {
      setShowUnlockModal(false);
      setPin('');
      // Navigate to admin
      if (instanceId) {
        navigate(`/admin?instance=${instanceId}`);
      } else {
        navigate('/admin');
      }
    } else {
      setError('Virheellinen PIN-koodi');
    }

    setLoading(false);
  };

  const handleCloseUnlock = () => {
    setShowUnlockModal(false);
    setPin('');
    setError('');
  };

  // When locked, show only the tiny unlock button
  if (isLocked) {
    return (
      <>
        <UnlockButton onClick={() => setShowUnlockModal(true)} title="Avaa ylläpitotila">
          <Lock size={12} />
        </UnlockButton>

        {showUnlockModal && (
          <UnlockModal onClick={handleCloseUnlock}>
            <UnlockBox onClick={(e) => e.stopPropagation()}>
              <UnlockTitle>
                <Lock size={20} />
                Avaa ylläpito
              </UnlockTitle>
              <UnlockSubtitle>
                Syötä ylläpitäjän PIN-koodi
              </UnlockSubtitle>

              <PinInput
                type="text"
                value={pin}
                onChange={(e) => {
                  setPin(e.target.value.toUpperCase());
                  setError('');
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleUnlock()}
                placeholder="PIN"
                autoFocus
                autoComplete="off"
                $error={!!error}
              />

              {error && <ErrorText>{error}</ErrorText>}

              <ButtonGroup>
                <Button onClick={handleCloseUnlock}>Peruuta</Button>
                <Button $primary onClick={handleUnlock} disabled={loading || !pin.trim()}>
                  {loading ? 'Tarkistetaan...' : 'Avaa'}
                </Button>
              </ButtonGroup>
            </UnlockBox>
          </UnlockModal>
        )}
      </>
    );
  }

  // Normal mode - show settings button
  return (
    <div ref={dropdownRef} style={inline ? { display: 'inline-flex' } : undefined}>
      <SettingsButton
        $inline={inline}
        $connected={isConnected}
        $hasInstance={!!instanceId}
        onClick={() => setShowDropdown(!showDropdown)}
        title={!instanceId ? 'Asetukset' : (isConnected ? 'Yhdistetty' : 'Ei yhteyttä')}
      >
        <Settings size={18} />
      </SettingsButton>

      {showDropdown && (
        <Dropdown>
          {instanceId && (
            <ConnectionStatus $connected={isConnected}>
              {isConnected ? 'Yhdistetty' : 'Ei yhteyttä'}
            </ConnectionStatus>
          )}

          <DropdownSection>
            <DropdownItem onClick={handleRefreshConnection}>
              <RefreshCw size={16} />
              Päivitä yhteys
            </DropdownItem>
            {onSettingsClick && (
              <DropdownItem onClick={() => { setShowDropdown(false); onSettingsClick(); }}>
                <Settings size={16} />
                Asetukset
              </DropdownItem>
            )}
          </DropdownSection>

          <DropdownSection>
            <DropdownItem $highlight onClick={handleAdminMode}>
              <Shield size={16} />
              Ylläpitotila
            </DropdownItem>
          </DropdownSection>

          {hasPinSession && (
            <DropdownSection>
              <DropdownItem onClick={handleSignOut}>
                <LogOut size={16} />
                {t('navigation.signOutShort')}
              </DropdownItem>
            </DropdownSection>
          )}
        </Dropdown>
      )}
    </div>
  );
};

export default GlobalSettings;
